import { supabase } from '../supabase/client';

/**
 * Upload image to Supabase Storage
 * @param {File} file - The image file to upload
 * @returns {Promise<{url: string|null, error: Error|null}>}
 */
export const uploadImage = async (file) => {
  try {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      throw new Error('File size too large. Maximum size is 5MB.');
    }

    // Generate unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
    const filePath = `students/${fileName}`;

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('student-pictures')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('student-pictures')
      .getPublicUrl(data.path);

    return { url: publicUrl, error: null };
  } catch (error) {
    console.error('Error uploading image:', error);
    return { url: null, error };
  }
};

/**
 * Delete image from Supabase Storage
 * @param {string} imageUrl - The full URL of the image to delete
 * @returns {Promise<{success: boolean, error: Error|null}>}
 */
export const deleteImage = async (imageUrl) => {
  try {
    // Extract path from URL
    const urlParts = imageUrl.split('/student-pictures/');
    if (urlParts.length < 2) {
      return { success: false, error: new Error('Invalid image URL') };
    }
    
    const filePath = urlParts[1];

    const { error } = await supabase.storage
      .from('student-pictures')
      .remove([`students/${filePath}`]);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('Error deleting image:', error);
    return { success: false, error };
  }
};

